"use client"

import { useState, useEffect } from "react"
import { Loader2, Stethoscope, Receipt, FileText, Pill } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { useToast } from "@/components/ui/use-toast"
import { supabase } from "@/utils/supabase/client"
import { AppointmentsHistoryTab } from "./history-tabs/appointments-history-tab"
import { PaymentsHistoryTab } from "./history-tabs/payments-history-tab"
import { RecordsHistoryTab } from "./history-tabs/records-history-tab"
import { PrescriptionsHistoryTab } from "./history-tabs/prescriptions-history-tab"
import { Patient, Appointment, Payment, MedicalRecord, Prescription, Admission } from "@/types"

interface PatientHistoryViewProps {
  patientId: string
}

interface HistoryData {
  patient: Patient;
  appointments: Appointment[];
  payments: Payment[];
  medical_records: MedicalRecord[];
  prescriptions: Prescription[];
  admissions: Admission[];
  timeline: any[];
}

export function PatientHistoryView({ patientId }: PatientHistoryViewProps) {
  const [data, setData] = useState<HistoryData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()

  useEffect(() => {
    const fetchHistory = async () => {
      if (!patientId) return
      setIsLoading(true)
      try {
        const { data: patientData, error: patientError } = await supabase
          .from("patients")
          .select("*")
          .eq("id", patientId)
          .single()

        if (patientError) throw patientError

        const { data: appointmentsData, error: appointmentsError } = await supabase
            .from("appointments")
            .select("*, doctors!inner(name, specialization)")
            .eq("patient_id", patientId)

        if (appointmentsError) throw appointmentsError

        const { data: paymentsData, error: paymentsError } = await supabase
            .from("payments")
            .select("*, appointments(appointment_date)")
            .eq("patient_id", patientId)
        
        if (paymentsError) throw paymentsError

        const { data: medicalRecordsData, error: medicalRecordsError } = await supabase
            .from("medical_records")
            .select("*, doctors!inner(name), appointments!inner(appointment_date)")
            .eq("patient_id", patientId)

        if (medicalRecordsError) throw medicalRecordsError

        const appointmentIds = appointmentsData?.map(a => a.id) || [];
        
        const { data: prescriptionsData, error: prescriptionsError } = await supabase
            .from("prescriptions")
            .select("*, appointments!inner(appointment_date)")
            .in("appointment_id", appointmentIds)

        if (prescriptionsError) throw prescriptionsError

        // Combine all data into a single sorted timeline
        const timeline = [
            ...(appointmentsData || []).map(item => ({ ...item, type: 'appointment', date: item.appointment_date })),
            ...(paymentsData || []).map(item => ({ ...item, type: 'payment', date: item.created_at })),
            ...(medicalRecordsData || []).map(item => ({ ...item, type: 'record', date: item.created_at })),
            ...(prescriptionsData || []).map(item => ({ ...item, type: 'prescription', date: item.created_at })),
        ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        setData({ 
            patient: patientData,
            appointments: appointmentsData || [],
            payments: paymentsData || [],
            medical_records: medicalRecordsData || [], 
            prescriptions: prescriptionsData || [],
            admissions: [],
            timeline,
        })
      } catch (error: any) {
        console.error("Error fetching patient history:", error)
        toast({
            title: "Error",
            description: "Could not fetch patient history.",
            variant: "destructive",
        })
      } finally {
        setIsLoading(false)
      }
    }
    fetchHistory()
  }, [patientId, toast])

  if (isLoading) {
    return (
        <div className="flex items-center justify-center rounded-lg border h-96">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
    )
  }

  if (!data || !data.patient) {
    return <p className="text-center text-muted-foreground mt-8">Could not load patient history.</p>
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>History for {data.patient.name}</CardTitle>
        <CardDescription>MR Number: {data.patient.mr_number} | Phone: {data.patient.phone}</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="appointments" className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
            <TabsTrigger value="appointments"><Stethoscope className="mr-2 h-4 w-4"/>Appointments ({data.appointments.length})</TabsTrigger>
            <TabsTrigger value="payments"><Receipt className="mr-2 h-4 w-4"/>Payments ({data.payments.length})</TabsTrigger>
            <TabsTrigger value="records"><FileText className="mr-2 h-4 w-4"/>Records ({data.medical_records.length})</TabsTrigger>
            <TabsTrigger value="prescriptions"><Pill className="mr-2 h-4 w-4"/>Prescriptions ({data.prescriptions.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="appointments" className="mt-4">
            <AppointmentsHistoryTab appointments={data.appointments} />
          </TabsContent>
          <TabsContent value="payments" className="mt-4">
            {data.payments && <PaymentsHistoryTab payments={data.payments as any} />}
          </TabsContent>
          <TabsContent value="records" className="mt-4">
            {data.medical_records && <RecordsHistoryTab records={data.medical_records.map(r => ({...r, type: 'record'})) as any} vitals={[]} />}
          </TabsContent>
          <TabsContent value="prescriptions" className="mt-4">
            <PrescriptionsHistoryTab prescriptions={data.prescriptions} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
} 